import { createContext, forwardRef, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { CSSProperties, HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn';

/*
 * Dense grid used by every list screen (design: bordered container radius 10 on bg-3; sticky
 * 34px header on bg-2 with 11px/600/0.06em uppercase fg-2 labels; rows min-height 42–44px with
 * a border-bottom and bg-1 hover). Columns are a CSS grid-template-columns string so each screen
 * reproduces its exact column set, e.g. `minmax(200px,1.4fr) 150px minmax(160px,1fr) 100px`.
 * Narrow windows: write a fixed design track `Npx` as `minmax(Mpx,Npx)`, M being the widest real
 * content plus padding. Grid sizing grows such tracks to N before fr tracks expand, so wide
 * windows keep the design; tracks must not depend on content (no auto / min-content), because
 * every row is a separate grid sharing the template.
 *
 * Keyboard: rows use a roving tabindex. ↑/↓ move between rows, Home/End jump, Enter (or a
 * click) activates the row, Space toggles selection when `onToggleSelect` is given.
 */

interface GridContextValue {
  readonly columns: string;
  readonly activeIndex: number;
  readonly setActiveIndex: (index: number) => void;
  readonly focusRow: (index: number) => void;
}

const GridContext = createContext<GridContextValue | null>(null);

function useGrid(): GridContextValue {
  const ctx = useContext(GridContext);
  if (!ctx) throw new Error('DataGrid parts must be rendered inside <DataGrid>');
  return ctx;
}

export interface DataGridProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  /** CSS grid-template-columns shared by the header, rows and totals. */
  readonly columns: string;
  /** Accessible name, e.g. "Invoices". */
  readonly label: string;
  /** Number of body rows; keeps the roving tab stop inside the list when rows disappear. */
  readonly rowCount?: number;
}

export function DataGrid({ columns, label, rowCount, className, children, ...rest }: DataGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const activeIndex = rowCount !== undefined ? Math.max(0, Math.min(active, rowCount - 1)) : active;

  const focusRow = useCallback((index: number) => {
    const row = containerRef.current?.querySelector<HTMLElement>(`[data-row-index="${index}"]`);
    if (!row) return;
    setActive(index);
    row.focus();
  }, []);

  const value = useMemo(
    () => ({ columns, activeIndex, setActiveIndex: setActive, focusRow }),
    [columns, activeIndex, focusRow],
  );

  return (
    <GridContext.Provider value={value}>
      <div
        ref={containerRef}
        role="grid"
        aria-label={label}
        aria-rowcount={rowCount !== undefined ? rowCount + 1 : undefined}
        className={cn('overflow-clip rounded-2 border border-border-1 bg-bg-3', className)}
        {...rest}
      >
        {children}
      </div>
    </GridContext.Provider>
  );
}

export interface DataGridHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Sticky offset inside the scrolling `main` (px). */
  readonly stickyTop?: number;
}

/** Sticky 34px header row. Children are DataGridHeaderCell elements. */
export function DataGridHeader({ stickyTop = 0, className, style, children, ...rest }: DataGridHeaderProps) {
  const { columns } = useGrid();
  return (
    <div
      role="row"
      className={cn(
        'sticky z-[3] grid h-[34px] items-center border-b border-border-1 bg-bg-2',
        'text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-2',
        className,
      )}
      style={{ gridTemplateColumns: columns, top: stickyTop, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface DataGridCellProps extends HTMLAttributes<HTMLDivElement> {
  readonly align?: 'left' | 'right' | 'center';
  /** First column uses 12px side padding; the others 10px. */
  readonly first?: boolean;
  /**
   * Clip overflowing text with an ellipsis. Plain-text children then double as the `title`
   * tooltip (unless `title` is given), so a clipped name or header stays readable.
   */
  readonly truncate?: boolean;
  /** CSS grid-column value, e.g. `span 6` for totals rows. */
  readonly span?: CSSProperties['gridColumn'];
}

function cellClass({
  align = 'left',
  first,
  truncate,
}: Pick<DataGridCellProps, 'align' | 'first' | 'truncate'>) {
  return cn(
    'min-w-0',
    first ? 'px-3' : 'px-2.5',
    align === 'right' && 'text-right',
    align === 'center' && 'text-center',
    truncate && 'truncate',
  );
}

/** Placeholder of an empty value; it never needs a tooltip. */
const EMPTY_VALUE = '—';

/**
 * The tooltip of a truncating cell: an explicit `title`, else its plain-text children, except
 * empty text and the "—" placeholder.
 */
function cellTitle({
  truncate,
  title,
  children,
}: Pick<DataGridCellProps, 'truncate' | 'title' | 'children'>) {
  if (title !== undefined || !truncate) return title;
  if (typeof children !== 'string' && typeof children !== 'number') return undefined;
  const text = String(children);
  const trimmed = text.trim();
  return trimmed === '' || trimmed === EMPTY_VALUE ? undefined : text;
}

export interface DataGridHeaderCellProps extends DataGridCellProps {
  /**
   * Column label that ends in an ellipsis once the column narrows, with the full text as the
   * `title`. It may run into the cell's trailing padding (10px) before it truncates, so a label
   * that just fits the design width renders as before. Replaces `children`.
   */
  readonly label?: string;
}

/** A label span allowed into the trailing padding (the right one, or the left when right-aligned). */
function HeaderLabel({
  label,
  align,
}: {
  readonly label: string;
  readonly align: DataGridCellProps['align'];
}) {
  return <span className={cn('block truncate', align === 'right' ? '-ml-2.5' : '-mr-2.5')}>{label}</span>;
}

export function DataGridHeaderCell({
  align,
  first,
  truncate,
  span,
  className,
  style,
  title,
  label,
  children,
  ...rest
}: DataGridHeaderCellProps) {
  return (
    <div
      role="columnheader"
      className={cn(cellClass({ align, first, truncate }), className)}
      style={span ? { gridColumn: span, ...style } : style}
      title={title ?? label ?? cellTitle({ truncate, title, children })}
      {...rest}
    >
      {label !== undefined ? <HeaderLabel label={label} align={align} /> : children}
    </div>
  );
}

export function DataGridCell({
  align,
  first,
  truncate,
  span,
  className,
  style,
  title,
  children,
  ...rest
}: DataGridCellProps) {
  return (
    <div
      role="gridcell"
      className={cn(cellClass({ align, first, truncate }), className)}
      style={span ? { gridColumn: span, ...style } : style}
      title={cellTitle({ truncate, title, children })}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface DataGridRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Position among body rows (0-based); required for keyboard navigation. */
  readonly index: number;
  /** Enter or click on the row (opens the record). */
  readonly onActivate?: () => void;
  /** Space on the focused row (toggles selection). */
  readonly onToggleSelect?: () => void;
  readonly selected?: boolean;
  /** Row min-height in px: 44 (default), 42 (Invoices), 36 (compact Close rows). */
  readonly minHeight?: number;
  /** Archived / paused rows render at reduced opacity. */
  readonly dimmed?: boolean;
  readonly children?: ReactNode;
}

export const DataGridRow = forwardRef<HTMLDivElement, DataGridRowProps>(function DataGridRow(
  {
    index,
    onActivate,
    onToggleSelect,
    selected,
    minHeight = 44,
    dimmed,
    className,
    style,
    onKeyDown,
    onClick,
    onFocus,
    children,
    ...rest
  },
  ref,
) {
  const { columns, activeIndex, setActiveIndex, focusRow } = useGrid();

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.target !== event.currentTarget) return;
    const container = event.currentTarget.closest('[role="grid"]');
    const count = container?.querySelectorAll('[data-row-index]').length ?? 0;
    const target =
      event.key === 'ArrowDown'
        ? Math.min(index + 1, count - 1)
        : event.key === 'ArrowUp'
          ? Math.max(index - 1, 0)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? count - 1
              : null;
    if (target !== null) {
      event.preventDefault();
      focusRow(target);
    } else if (event.key === 'Enter' && onActivate) {
      event.preventDefault();
      onActivate();
    } else if (event.key === ' ' && onToggleSelect) {
      event.preventDefault();
      onToggleSelect();
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) onActivate?.();
  };

  return (
    <div
      ref={ref}
      role="row"
      data-row-index={index}
      aria-selected={onToggleSelect ? !!selected : undefined}
      tabIndex={index === activeIndex ? 0 : -1}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onFocus={(event) => {
        onFocus?.(event);
        if (event.target === event.currentTarget) setActiveIndex(index);
      }}
      className={cn(
        'grid items-center border-b border-border-1 hover:bg-bg-1 focus-visible:rounded-none focus-visible:outline-offset-[-2px]',
        onActivate && 'cursor-pointer',
        selected && 'bg-pi-navy-tint',
        dimmed && 'opacity-55',
        className,
      )}
      style={{ gridTemplateColumns: columns, minHeight, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
});
